<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <dialog ref="dialogRef" role="dialog" aria-modal="true">
    <div class="content-wrapper">
      <div ref="contentRef" class="content" :style="footerGapStyle" v-bind="$attrs">
        <AppModalDialogAssistantBanner />
        <AppModalDialogHeader :no-close-button :left-header :on-close="hideModal">
          <template #left>
            <slot name="header-left" />
          </template>
          <slot name="header" />
        </AppModalDialogHeader>
        <AppModalDialogMain>
          <slot />
        </AppModalDialogMain>
        <AppModalDialogFooter :has-content :timeout :timer-key :on-timeout="hideModal">
          <slot name="footer" />
        </AppModalDialogFooter>
        <AppModalDialogBusyOverlay :is-busy :busy-message />
      </div>
    </div>
  </dialog>
</template>

<script setup>
import {onClickOutside, useEventListener} from "@vueuse/core";
import {computed, nextTick, onMounted, provide, ref, useSlots, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppModalDialogHeader from "./Header";
import AppModalDialogMain from "./Main";
import AppModalDialogAssistantBanner from "./AssistantBanner";
import AppModalDialogFooter from "./Footer";
import AppModalDialogBusyOverlay from "./BusyOverlay";
import {useDialogFocusTrap} from "@/composables/useDialogFocusTrap";

const props = defineProps([
  "hash",
  "closeHash",
  "timeout",
  "openDelay",
  "noCloseButton",
  "leftHeader",
  "isBusy",
  "busyMessage",
  "trapsFocus", // keyboard focus kept inside while open, and back to the opener after (Profile)
  "isHeldOpen", // not closed by ✕, Escape or a click outside while true (Subscriptions: saving)
  "footerGap" // the space from the content to the footer's buttons, if not the standard 32px
]);
const emit = defineEmits(["closed"]);
const [dialogRef, timerKey] = [ref(null), ref(0)];
const [route, router] = [useRoute(), useRouter()];
const contentRef = ref(null);
const slots = useSlots();
const focusTrap = useDialogFocusTrap(dialogRef);
const hasContent = computed(() => typeof slots.footer === "function");
// the standard content-to-footer gap, 32px (Seán, 2026-10-23: as #subscriptions), unless given
const footerGapStyle = computed(() => {
  return props.footerGap ? {"--dialog-footer-gap": props.footerGap} : {};
});

defineOptions({inheritAttrs: false});

const isScrolled = ref(false);

function handleMainScroll(event) {
  const scrollTop = event?.target?.scrollTop || 0;
  const nextIsScrolled = scrollTop > 2;
  if (nextIsScrolled === isScrolled.value) return;
  isScrolled.value = nextIsScrolled;
}

provide("isScrolled", isScrolled);
provide("handleMainScroll", handleMainScroll);

const match = computed(() => {
  if (!props.hash) return false;
  const currentHash = route.hash.slice(1);
  if (!currentHash) return false;
  if (typeof props.hash === "string") return currentHash === props.hash;
  if (props.hash instanceof RegExp) return props.hash.test(currentHash);
  return false;
});

async function checkHash() {
  if (!props.hash) return;
  if (!match.value) {
    const modal = dialogRef?.value;
    if (modal?.open) hideModal();
    return;
  }
  if (props.openDelay !== undefined) {
    await new Promise((resolve) => setTimeout(resolve, 1000 * props.openDelay));
  }
  showModal();
}

function showModal() {
  document.body.style.overflow = "hidden";
  const modal = dialogRef?.value;
  if (!modal) return console.error("Modal: dialogRef not defined.");
  if (modal.open) return; // already open, e.g. #profile switching tabs (its hash changes)
  if (props.trapsFocus) focusTrap.rememberOpener();
  modal.showModal();
  timerKey.value++;
}

function closeAndNavigate(targetHash) {
  document.body.style.overflow = "";
  const modal = dialogRef?.value;
  if (!modal || !modal.open) return;
  modal.close();
  if (props.trapsFocus) focusTrap.restoreFocus();
  emit("closed");
  const currentHash = route.hash.startsWith("#") ? route.hash.slice(1) : "";
  const isExactMatch = typeof props.hash === "string" && currentHash === props.hash;
  const isPatternMatch = props.hash instanceof RegExp && props.hash.test(currentHash);
  if ((isExactMatch || isPatternMatch) && route.hash !== "") {
    router.replace({path: route.path, hash: targetHash});
  }
}

function hideModal() {
  if (props.isHeldOpen) return;
  closeAndNavigate(props.closeHash ? `#${props.closeHash}` : "");
}

function dismissModal() {
  if (props.isHeldOpen) return;
  closeAndNavigate("");
}

function onDialogCancel(event) {
  event.preventDefault();
  event.stopPropagation();
  hideModal();
}

function onDialogKeydown(event) {
  if (props.trapsFocus) focusTrap.onKeydown(event);
  if (event.key !== "Escape") return;
  event.preventDefault();
  event.stopPropagation();
  hideModal();
}

onMounted(async () => {
  await nextTick();
  onClickOutside(contentRef, dismissModal);
  useEventListener(dialogRef, "cancel", onDialogCancel);
  useEventListener(dialogRef, "keydown", onDialogKeydown);
  // a Tab while the focus is on the page (e.g. its control was removed as a step changed): the
  // dialog doesn't hear it, so it's caught here and sent back into the dialog; not a key pressed in
  // another open dialog (e.g. Sign In over Profile), which is that dialog's
  useEventListener(document, "keydown", (event) => {
    if (!props.trapsFocus || event.target?.closest?.("dialog[open]")) return;
    focusTrap.onKeydown(event);
  });
  useEventListener(dialogRef, "focusout", (event) => {
    if (props.trapsFocus) focusTrap.onFocusOut(event);
  });
  checkHash();
});

watch(
  () => route.hash,
  (newHash, oldHash) => {
    if (newHash !== oldHash) checkHash();
  }
);
</script>

<style scoped>
dialog {
  position: fixed;
  pointer-events: auto;
  border: none;
  width: 100dvw;
  height: 100dvh;
  padding: 0;
  margin: 0;
  transform: translate(-50%, -50%);
  z-index: 100;
  inset: 50%;
  background-color: rgba(0, 0, 0, 0.005);
  backdrop-filter: blur(1px);
}
dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.75) !important;
  backdrop-filter: blur(1px);
}
.content-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
}
.content {
  position: relative;
  display: flex;
  flex-direction: column;
  /* Seán's spec for #sign-in (2026-10-22), every dialog's since 2026-10-23: 382px wide, 24px
     at the sides (a dialog may set its own width and --dialog-padding-x: Profile) */
  box-sizing: border-box;
  min-width: 0;
  width: min(382px, calc(100vw - 32px));
  max-height: min(700px, 95dvh);
  overflow: hidden;
  /* every dialog's: none sets its own (DESIGN.md § B.1) */
  background: var(--dialog-background, #e4e4e4);
  border-radius: 12px;
  padding: 0 var(--dialog-padding-x, 24px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  color: #333;
}
.content button {
  pointer-events: auto;
}
/* focus, every dialog (from #profile's, 2026-10-14; fullstack FORMS.md § 17; Seán, 2026-10-23):
   one solid ring, only when the browser judges it useful (:focus-visible: the keyboard, not a
   click or tap). The site's dotted keyboard mark (global.css .showFocusOutline,
   useFocusHandlers.js) stayed after a click once Tab had been used: never in a dialog. Text
   fields keep their own focus border */
.content :deep(.showFocusOutline:not(:focus-visible)) {
  outline: none !important;
}
.content :deep(:is(button, a, [role="switch"], [role="tab"], [tabindex="0"]):focus-visible) {
  outline: 2px solid var(--brand-dark-color) !important;
  outline-offset: 2px;
}
.content :deep(button[type="submit"]) {
  background: #3ddc97;
  color: #fff;
  font-weight: bold;
  border: none;
  box-shadow: none;
  width: 100%;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
  display: block;
  text-align: center;
}
.content :deep(button[type="submit"]:disabled) {
  background: #aaa;
  color: #fff;
  opacity: 0.6;
  cursor: not-allowed;
}
/* a footer's note on what the button does (#sign-in, the email change, Send Email): fine print,
   not for reading closely, so a step more muted than body text (Seán, 2026-11-03) */
.content :deep(footer .note) {
  color: #757575; /* 3.8:1 on the dialog grey: #666 was 4.6:1, the AA line for text */
}
.content :deep(.footer-buttons) {
  display: flex;
  gap: 0.75rem;
}
/* side by side, each the footer's usual button (Footer.vue; no longer pills: Seán, 2026-10-23) */
.content :deep(.footer-buttons button) {
  flex: 1;
  width: auto;
}
.content :deep(.footer-buttons button[type="button"]) {
  background: #cdcdcd;
  color: #333;
  border: none;
  box-shadow: none;
  font-weight: normal;
}
</style>
